// SPDX-License-Identifier: GPL-3.0-or-later
// Which dialog opens when, and what the game does first. These are pure functions of the page
// state: they read no DOM and keep no state. The page asks them, then acts on the answer.
//
// Rules:
// - A dialog that a player opens from the header menu during play (How to play, Settings,
//   Featured turns) pauses the game first, so the ball does not move under the text.
// - The start and game over dialogs have a button for every way out. Escape does not close them.
// - The Menu button of a pad must not close the pause dialog under an open overlay dialog.

import type { GamePhase } from "../src/shared/contracts.ts";
import { isGameActive } from "./screens.ts";

/** The dialogs of the game page. */
export type DialogId = "start" | "pause" | "over" | "howto" | "settings" | "featured";

/** The part of a dialog that the policy reads. A real `HTMLDialogElement` fits it. */
export interface DialogState {
  readonly open: boolean;
}

/** The dialogs that a player opens during play. They sit above the pause dialog. */
export const OVERLAY_DIALOGS: readonly DialogId[] = ["howto", "settings", "featured"];

/** Where an open request comes from: the header menu, or a button inside another dialog. */
export type OpenSource = "menu" | "dialog";

/** The set of the dialogs that are open now. */
export function openSet(dialogs: Readonly<Record<DialogId, DialogState>>): ReadonlySet<DialogId> {
  const ids = Object.keys(dialogs) as DialogId[];
  return new Set(ids.filter((id) => dialogs[id].open));
}

/** True when a dialog that a player opens during play is open. */
export function overlayDialogOpen(open: ReadonlySet<DialogId>): boolean {
  return OVERLAY_DIALOGS.some((id) => open.has(id));
}

/** True when the game can pause now: a game runs and it does not wait already. */
export function canPause(phase: GamePhase, paused: boolean): boolean {
  return !paused && isGameActive(phase);
}

/** True when the game must pause before this dialog opens. */
export function pausesFirst(
  dialog: DialogId,
  source: OpenSource,
  phase: GamePhase,
  paused: boolean,
): boolean {
  return source === "menu" && OVERLAY_DIALOGS.includes(dialog) && canPause(phase, paused);
}

/** What the Menu button of a pad does. */
export type MenuButtonAction = "toggle_pause" | "ignore";

/** The Menu button must not close the pause dialog under an open overlay dialog. */
export function menuButtonAction(open: ReadonlySet<DialogId>): MenuButtonAction {
  return overlayDialogOpen(open) ? "ignore" : "toggle_pause";
}

/** What the start button of a pad does. */
export type PadStartAction = "start_free_game" | "send_start" | "ignore";

/**
 * The start button of a pad starts a game on the start and game over screens. During play (no
 * dialog) it goes to the engine as the start input. Any other dialog takes the button.
 */
export function padStartAction(open: ReadonlySet<DialogId>): PadStartAction {
  if (overlayDialogOpen(open)) return "ignore";
  if (open.has("start") || open.has("over")) return "start_free_game";
  return open.size === 0 ? "send_start" : "ignore";
}

/** True when Escape may close the dialog. The start and game over dialogs stay open. */
export function escapeCloses(dialog: DialogId): boolean {
  return dialog !== "start" && dialog !== "over";
}
